import { useEffect, useRef } from 'react'

// Fluid animated gradient orbs (canvas). Brand-coloured glows drift on sine paths
// with additive blending → organic flowing aurora.
// Respects prefers-reduced-motion (renders a single static frame, no loop).

type RGB = [number, number, number]

type Orb = {
  color: RGB // dark-mode color
  colorLight: RGB // light-mode color (more saturated/deeper — additive blend is off on light)
  radius: number // fraction of min(w,h)
  ax: number // sine amplitude x (fraction of w)
  ay: number // sine amplitude y (fraction of h)
  cx: number // center x (fraction of w)
  cy: number // center y (fraction of h)
  sx: number // angular speed x
  sy: number // angular speed y
  phase: number
}

const ORBS: Orb[] = [
  { color: [37, 79, 113], colorLight: [37, 79, 113], radius: 0.55, ax: 0.14, ay: 0.1, cx: 0.24, cy: 0.22, sx: 0.11, sy: 0.14, phase: 0 },
  { color: [148, 200, 228], colorLight: [56, 132, 178], radius: 0.5, ax: 0.16, ay: 0.12, cx: 0.78, cy: 0.28, sx: 0.13, sy: 0.09, phase: 1.7 },
  { color: [56, 189, 248], colorLight: [14, 132, 190], radius: 0.42, ax: 0.12, ay: 0.15, cx: 0.68, cy: 0.82, sx: 0.1, sy: 0.12, phase: 3.1 },
  { color: [99, 102, 241], colorLight: [79, 70, 190], radius: 0.4, ax: 0.15, ay: 0.11, cx: 0.18, cy: 0.8, sx: 0.09, sy: 0.13, phase: 4.6 },
  { color: [125, 211, 252], colorLight: [45, 140, 185], radius: 0.34, ax: 0.13, ay: 0.13, cx: 0.5, cy: 0.5, sx: 0.12, sy: 0.1, phase: 2.2 },
]

export default function LoginBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    let w = 0
    let h = 0
    let dpr = 1
    let dark = document.documentElement.classList.contains('dark')

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2)
      w = canvas.clientWidth
      h = canvas.clientHeight
      canvas.width = Math.floor(w * dpr)
      canvas.height = Math.floor(h * dpr)
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    }

    const drawOrb = (x: number, y: number, r: number, color: [number, number, number], alpha: number) => {
      const g = ctx.createRadialGradient(x, y, 0, x, y, r)
      const [cr, cg, cb] = color
      g.addColorStop(0, `rgba(${cr},${cg},${cb},${alpha})`)
      g.addColorStop(0.6, `rgba(${cr},${cg},${cb},${alpha * 0.35})`)
      g.addColorStop(1, `rgba(${cr},${cg},${cb},0)`)
      ctx.fillStyle = g
      ctx.beginPath()
      ctx.arc(x, y, r, 0, Math.PI * 2)
      ctx.fill()
    }

    const render = (t: number) => {
      const m = Math.min(w, h)
      ctx.clearRect(0, 0, w, h)
      // Dark: additive blend for a glowing aurora. Light: plain over-compositing
      // with deeper, more opaque orbs (additive would wash out to white).
      ctx.globalCompositeOperation = dark ? 'lighter' : 'source-over'
      const alpha = dark ? 0.5 : 0.32

      for (const o of ORBS) {
        const x = (o.cx + Math.sin(t * o.sx + o.phase) * o.ax) * w
        const y = (o.cy + Math.cos(t * o.sy + o.phase) * o.ay) * h
        drawOrb(x, y, o.radius * m, dark ? o.color : o.colorLight, alpha)
      }

      ctx.globalCompositeOperation = 'source-over'
    }

    let raf = 0
    const loop = (now: number) => {
      render(now / 1000)
      raf = requestAnimationFrame(loop)
    }

    // Re-theme live if the user toggles dark mode while on the login page.
    const themeObserver = new MutationObserver(() => {
      const next = document.documentElement.classList.contains('dark')
      if (next !== dark) {
        dark = next
        if (reduce) render(0)
      }
    })
    themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })

    resize()
    window.addEventListener('resize', resize)

    if (reduce) {
      render(0)
    } else {
      raf = requestAnimationFrame(loop)
    }

    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', resize)
      themeObserver.disconnect()
    }
  }, [])

  return <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" aria-hidden="true" />
}
